import type { Chat } from './plan';

/**
 * **되묻기 전에 사람처럼 판정 기준을 세운다.**
 *
 * 「요 근래 입사하신 분들이 경력직 위주인지 비율 알려줘」에 계획이 「경력직 구분이 없다」며 어느 화면에
 * 있느냐고 되물었다(2026-10-21). 계획은 질문의 낱말을 칸 이름에 맞춰 볼 뿐이라, 맞는 칸이 없으면 멈춘다.
 * 사람은 그러지 않는다 — 인사등록 경력 탭에 앞 회사 기록이 있으면 경력직이라 보고, 그렇게 셌다고 밝힌다.
 *
 * 계획이 되물으려 할 때 한 번, 그 기준과 자료 길을 따로 생각하게 한다. 계획은 JSON 만 내느라 이 생각을
 * 할 자리가 없다. 기준을 세울 수 없으면(재료가 어디에도 없거나 뜻이 갈려 고를 수 없으면) 원래대로 묻는다.
 */
export type Derivation = {
  /** 무엇을 무엇으로 판정했는가 — 사람 말 한 문장. 답이 이것을 밝힌다. */
  criterion: string;
  /** 어느 화면·표·칸을 어떤 순서로 읽고 어떻게 잇는가. 다음 계획에 간다. */
  path: string;
  /** 사람이 말하지 않아 우리가 정한 범위(「요 근래」 = 최근 몇 달 등). */
  assumptions?: string;
};

const PROMPT = `너는 이 회사 ERP 를 오래 써 온 사무직 직원이다. 동료가 질문을 했고, 조회를 짜는 단계가
아래처럼 되물으려 한다. 되묻기 전에, 네가 그 자리에 있었다면 아래 화면들로 어떻게 답했을지 생각하라.

질문: {question}

되물으려던 말: {asked}
그 까닭: {why}

할 일:
1. 질문이 말하는 것 가운데 화면 칸 이름에 그대로 없는 것을 찾는다.
2. 그것을 아래 화면에 실제로 있는 칸·탭·표로 판정할 기준을 세운다. 그 일을 하는 직원이 들으면
   수긍할 기준이어야 한다. 예: 「단골 거래처」라는 칸이 없으면 「최근 1년 매출 건수가 많은 거래처」로 본다.
3. 어느 화면의 어느 표(탭)를 어떤 순서로 읽고, 두 표를 어느 칸으로 잇는지 적는다. 위 표 행마다 상세를
   읽어야 하면 펼쳐 읽기(sweep)를 쓰고, 앞 조회가 뽑은 대상만 읽으려면 matchFrom 으로 좁힌다.
4. 사람이 말하지 않은 범위(기간 등)는 그 말을 쓰는 사람이 흔히 뜻하는 값으로 정하고 적는다.
   「요 근래」·「요즘」·「최근」처럼 기간 없이 말하면 오늘까지 최근 3개월로 정한다.
   달로 나누는 화면(발생월 등)이면 이번 달까지 세 달로, 그 화면의 달 칸으로 센다고 적는다 — 화면 숫자와 같게.

기준을 세울 수 없는 때는 둘뿐이다 — 아래 화면 어디에도 판정할 재료가 없을 때, 또는 질문의 뜻이 둘
이상으로 갈려 답이 크게 달라지는데 흔히 쓰는 쪽을 고를 수 없을 때.

화면:
{screens}

JSON 한 덩이만 낸다.
세울 수 있으면 {"answerable":true,"criterion":"무엇을 무엇으로 판정했는가, 사람 말 한 문장","path":"화면·표·칸과 순서","assumptions":"우리가 정한 범위"}
없으면 {"answerable":false,"why":"한 문장"}`;

export function parseDerivation(said: string): Derivation | undefined {
  try {
    const json = /\{[\s\S]*\}/.exec(said)?.[0];
    const parsed = json ? JSON.parse(json) as Record<string, unknown> : undefined;
    if (parsed?.answerable !== true) return undefined;
    const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
    const criterion = text(parsed.criterion);
    const path = text(parsed.path);
    if (!criterion || !path) return undefined;
    const assumptions = text(parsed.assumptions);
    return { criterion, path, ...(assumptions ? { assumptions } : {}) };
  } catch {
    return undefined;
  }
}

export async function deriveCriterion(input: {
  question: string;
  asked: { question: string; reason: string };
  /** 계획이 본 화면 설명(`describe`). 같은 것을 봐야 같은 길을 짠다. */
  screens: string;
  chat: Chat;
}): Promise<Derivation | undefined> {
  // 함수로 넘긴다 — 글자로 넘기면 질문·화면 설명 속 `$&`·`$1` 이 치환 패턴으로 읽힌다.
  const prompt = PROMPT
    .replace('{question}', () => input.question)
    .replace('{asked}', () => input.asked.question)
    .replace('{why}', () => input.asked.reason || '없음')
    .replace('{screens}', () => input.screens);
  try {
    return parseDerivation(await input.chat(prompt, { step: 'reason' }));
  } catch {
    // 추론은 덤이다. 실패하면 원래 되묻기가 나간다.
    return undefined;
  }
}

/** 다음 계획에 실을 말. */
export function derivationNote(derived: Derivation): string {
  return `

직전 계획은 되물으려 했지만, 사람이라면 스스로 판정할 길이 있다.
판정 기준: ${derived.criterion}
자료 길: ${derived.path}${derived.assumptions ? `\n정한 범위: ${derived.assumptions}` : ''}
이 기준과 길로 조회 계획을 내라. 되묻지 마라.`;
}

/** 계산과 답에 실을 말. 답은 무엇으로 셌는지 밝혀야 한다 — 사람이 기준을 보고 고칠 수 있다. */
export function derivationFact(derived: Derivation): string {
  return `질문의 개념이 화면에 그대로 없어 우리가 판정 기준을 세웠다: ${derived.criterion}`
    + `${derived.assumptions ? ` (정한 범위: ${derived.assumptions})` : ''}.`
    + ' 답에 이 기준과 범위를 한 줄로 밝혀라 — 다르게 보면 그 기준으로 다시 세겠다고 덧붙여라.';
}

const VAGUE_PERIOD = /요\s*근래|요즘|최근/;
const SAID_PERIOD = /\d+\s*(?:년|월|일|주|개월|달|분기)|오늘|어제|이번|지난|저번|올해|작년|금년|전년|상반기|하반기|부터|까지/;

/**
 * **「요 근래」를 언제로 셌는지 답에 넘긴다.** 「요 근래」·「요즘」·「최근」은 오늘까지 최근 3개월이다(사용자, 2026-10-22).
 * 계획은 그렇게 조회하지만 답 단계는 그 날짜를 몰라 「요 근래의 범위가 따로 정해지지 않아」라고 했다.
 * 런타임이 날짜와 달을 계산해 넘긴다 — 모델에게 날짜 셈을 맡기지 않는다.
 */
export function vaguePeriodFact(question: string, today: string): string | undefined {
  if (!VAGUE_PERIOD.test(question) || SAID_PERIOD.test(question)) return undefined;
  const [year = 0, month = 0, day = 0] = today.split('-').map(Number);
  const iso = (date: Date) => date.toISOString().slice(0, 10);
  const start = iso(new Date(Date.UTC(year, month - 4, day + 1)));
  const first = new Date(Date.UTC(year, month - 3, 1));
  const firstYear = first.getUTCFullYear();
  const firstMonth = first.getUTCMonth() + 1;
  const months = firstYear === year
    ? `${year}년 ${firstMonth}~${month}월`
    : `${firstYear}년 ${firstMonth}월~${year}년 ${month}월`;
  return `질문의 「요 근래」(요즘·최근)는 오늘까지 최근 3개월로 셌다 — 날짜로 나누는 화면이면 ${start}~${today},`
    + ` 달로 나누는 화면이면 ${months}. 답 첫머리에 실제로 센 기간을 이 날짜나 달로 밝혀라.`
    + ' 범위가 정해지지 않았다고 말하지 마라.';
}
